import React from "react";

// Mui Styles
import ImageZoom from "@/components/imageZoom";
import { StyledPaper } from "@/styles/mui/globalStyles";
import { TableListTheme } from "@/styles/mui/tableListTheme";
import { ThemeProvider } from "@mui/material/styles";

// Navigate
import { useNavigate, useParams } from "react-router-dom";

// Components
import DataTable from "@/components/dataTable";
import ManningsLandingResponseDialog from "@/components/Dialog/manningsLandingResponseDialog";
import Layout from "@/components/layout";
import Title from "@/components/title";

// Fetch
import {
  getAvailableShopyDateRange,
  getLandingResponse,
  updateLandingResponse,
} from "@/fetch/fetchLanding";
import { GridToolbar } from "@mui/x-data-grid";

// Moment
import _ from "lodash";
import moment from "moment";
import "moment/locale/zh-hk";
import connectConfig from "../config/connectConfig";

const ManningsLandingResponsePage = (props) => {
  const { open, setOpen } = props;
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [availableShop, setAvailableShop] = React.useState([]);
  const [allShopList, setAllShopList] = React.useState([]);
  const [responseData, setResponseData] = React.useState(null);
  const [snackSeverity, setSnackSeverity] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [dataTableLoading, setDataTableLoading] = React.useState(false);

  const { id } = useParams();
  const [landingData, setLandingData] = React.useState({ response: [] });

  // Navigate
  const navigate = useNavigate();
  const ManingsLandingTableColumn = [
    {
      field: "name",
      headerName: "姓名",
      flex: 1,
      valueGetter: (value, row) => {
        return `${row.name} ${row.title_string}`;
      },
      valueFormatter: (value) => value,
    },
    {
      field: "phone",
      headerName: "電話",
      flex: 1,
    },
    {
      field: "age_range",
      headerName: "年齡",
      flex: 1,
    },
    {
      field: "pick_up_date",
      headerName: "預約日期",
      flex: landingData.is_immediately ? 1 : 2,
      valueGetter: (value, row) => {
        if (row.pick_up_date) {
          return `${moment(row.pick_up_date).format("YYYY-MM-DD")} ${moment(
            row.pick_up_start_time,
            "HH:mm:ss"
          ).format("A hh:mm")} - ${moment(
            row.pick_up_end_time,
            "HH:mm:ss"
          ).format("A hh:mm")}`;
        } else {
          return "即場拿取";
        }
      },
      valueFormatter: (value) => value,
    },
    {
      field: "contact_promoter_name",
      headerName: "負責/聯絡員工",
      flex: 1,
      valueGetter: (value, row) => {
        if (row.is_picked) {
          return `${row.register_promoter_name} (${row.register_promoter_staff_code})`;
        } else {
          if (!row.pick_up_date) {
            return "－－";
          } else {
            return `${row.contact_promoter_name} (${row.contact_promoter_staff_code})`;
          }
        }
      },
      valueFormatter: (value) => value,
    },
    {
      field: "work_name",
      headerName: "預約/領取地點",
      flex: 1,

      valueGetter: (value, row) => {
        if (row.is_picked) {
          return `${row.actual_pick_up_work_name}(${row.actual_pick_up_work_shortcode})`;
        } else {
          if (!row.pick_up_date) {
            return "－－";
          } else {
            return `${row.work_name}(${row.work_shortcode})`;
          }
        }
      },
      valueFormatter: (value) => value,
    },
    {
      field: "optin",
      headerName: "Opt-in",
      width: 70,
      valueGetter: (value, row) => {
        return row.optin ? "同意" : "不同意";
      },
      valueFormatter: (value) => value,
    },
    {
      field: "is_picked",
      headerName: "狀態",
      width: 50,
      valueGetter: (value, row) => {
        return row.is_picked ? "已取" : "未取";
      },
      valueFormatter: (value) => value,
    },
    {
      field: "submitted_at",
      headerName: "提交／領取時間",
      width: 200,

      renderCell: (params) => {
        if (params.row.is_picked) {
          return moment(params.row.time_purchased).format(
            "YYYY-MM-DD HH:mm:ss"
          );
        } else {
          return moment(params.row.submitted_at).format("YYYY-MM-DD HH:mm:ss");
        }
      },
    },
    {
      field: "item_string",
      headerName: "領取贈品",
      width: 150,
    },
    {
      field: "attachments",
      headerName: "簽名/相片",
      flex: 1,
      valueGetter: (value, row) => {
        if (_.isArray(_.values(row.attachments))) {
          const appendS3 = _.map(
            _.values(row.attachments),
            (attachment) => `${connectConfig.s3}/${attachment}`
          );
          return appendS3.join(",");
        } else {
          return row.attachments;
        }
      },
      renderCell: (params) => {
        return (
          <ImageZoom
            attachments={params.row.attachments ?? []}
            icon={true}
          ></ImageZoom>
        );
      },
    },
  ];

  // States
  const onSubmit = async () => {
    try {
      const body = {
        contact_promoter: responseData.contact_promoter,
        pick_up_location_id: responseData.pick_up_location_id,
        pick_up_date: responseData.pick_up_date,
        pick_up_start_time: moment(
          responseData.pick_up_start_time,
          "HH:mm:ss"
        ).format("HH:mm"),
        pick_up_end_time: moment(responseData.pick_up_start_time, "HH:mm:ss")
          .add(1, "hour")
          .format("HH:mm"),
      };
      await updateLandingResponse(
        responseData.landing_id,
        responseData.id,
        body
      );
      setSnackMessage("更新成功");
      setSnackSeverity("success");
    } catch (error) {
      setSnackMessage("更新失敗");
      setSnackSeverity("error");
    } finally {
      setSnackOpen(true);
      setDialogOpen(false);
      fetchAll();
    }
  };
  const fetchAll = async () => {
    try {
      setDataTableLoading(true);
      const { data } = await getLandingResponse(id);
      setLandingData(data);

      const { data: shops } = await getAvailableShopyDateRange(
        data.start_date,
        data.end_date
      );
      const uniqByLocationId = _.uniqBy(shops, "location_id");
      const autoCompleteOption = _.map(uniqByLocationId, (v) => {
        return {
          label: `${v.work_name} (${v.work_shortcode})`,
          value: String(v.location_id),
          category: v.district_name,
        };
      });
      setAvailableShop(autoCompleteOption);
      setAllShopList(shops);
    } catch (error) {
    } finally {
      setDataTableLoading(false);
    }
  };
  React.useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const Toolbar = () => (
    <GridToolbar
      csvOptions={{
        utf8WithBom: true,
        fileName: "Manning Landings Response",
      }}
    />
  );
  const getRowId = (row) => {
    return row.internalId;
  };
  return (
    <Layout
      header="ManningsLandingResponsePage"
      open={open}
      setOpen={setOpen}
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      snackSeverity={snackSeverity}
    >
      <Title history={() => navigate(-1)} title={landingData.name} />
      <StyledPaper page={"yes"}>
        <ThemeProvider theme={TableListTheme}>
          <DataTable
            loading={dataTableLoading}
            Toolbar={Toolbar}
            rows={landingData.response}
            columns={ManingsLandingTableColumn}
            onRowClick={({ row }) => {
              if (!row.is_picked) {
                setResponseData(row);
                setDialogOpen(true);
              }
            }}
            getRowID={getRowId}
            pageSize={10}
            getRowHeight={() => "auto"}
          />
        </ThemeProvider>
      </StyledPaper>
      {dialogOpen && (
        <ManningsLandingResponseDialog
          allShopList={allShopList}
          open={dialogOpen}
          availableShop={availableShop}
          submitMessage={"更新"}
          cancelMessage={"取消"}
          onSubmit={onSubmit}
          responseData={responseData}
          setResponseData={setResponseData}
          onCancel={() => setDialogOpen(false)}
        ></ManningsLandingResponseDialog>
      )}
    </Layout>
  );
};

export default ManningsLandingResponsePage;
